import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Box, Button, CircularProgress, Snackbar } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import FacebookIcon from '@/client/assets/icons/facebook.png';
import InstagramIcon from '@/client/assets/icons/instagram.svg';
import LineIcon from '@/client/assets/icons/line.png';
import LinkIcon from '@/client/assets/icons/link.svg';
import Result0Svg from '@/client/assets/images/tb/result_0_40.svg';
import Result60Svg from '@/client/assets/images/tb/result_60.svg';
import Result80Svg from '@/client/assets/images/tb/result_80.svg';
import Result100Svg from '@/client/assets/images/tb/result_100.svg';
import PrizeDrawnSvg from '@/client/assets/images/tb/result_100_drawn.svg';
import PrizeFullSvg from '@/client/assets/images/tb/result_100_end.svg';
import { trackShareQuiz } from '@/client/utils/ga';
import { preloadImage } from '@/client/utils/image';
import Liff, { getLineId } from '@/client/utils/liff';
import { copyToClipboard } from '@/client/utils/text';
import { PRIZE_STATUS } from './constants';
import styles from './styles';

const SERVER_URL = __SERVER_URL__;
const testAgainBadges = [Result0Svg, Result0Svg, Result0Svg, Result60Svg, Result80Svg];
const prizeBadges = {
  [PRIZE_STATUS.PRIZE_DRAWN]: PrizeDrawnSvg,
  [PRIZE_STATUS.PRIZE_FULL]: PrizeFullSvg,
  [PRIZE_STATUS.PRIZE_INIT]: Result100Svg,
};

const useStyles = makeStyles(createStyles(styles));

const Result = ({ config }) => {
  const { id, partNum } = config;

  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = useState(true);

  const { correctNum } = location.state;

  const saveRecord = async () => {
    try {
      const lineId = await getLineId();
      const resp = await fetch(`${SERVER_URL}/disease-quiz/record`, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'POST',
        body: JSON.stringify({ lineId, diseaseId: id, partNum, questionNum: correctNum }),
      });
      if (!resp.ok) {
        throw new Error(`status: ${resp.status}`);
      }
      history.replace(`/disease-quiz-${id}/prize?liffId=${Liff.id}`);
      setLoading(false);
    } catch (error) {
      alert('非常抱歉，作答紀錄儲存失敗，請再試一次');
      Liff.closeWindow();
      throw new Error(`failed to save record: ${error}`);
    }
  };

  useEffect(() => {
    saveRecord();
  }, []);

  if (loading) {
    return (
      <Box padding={2} display="flex" justifyContent="center" width="100vw">
        <CircularProgress />
      </Box>
    );
  }

  return <Box />;
};

export const ShareButtons = ({ config }) => {
  const { id } = config;

  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const { full: fullLiffId } = __LIFF_ID__;
  const shareURL = `https://liff.line.me/${fullLiffId}/disease-quiz-${id}?liffId=${fullLiffId}`;
  const shareData = { title: document.title, url: shareURL };

  return (
    <Box className={classes.shareButtons}>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        autoHideDuration={2500}
        message="連結已複製。"
      />
      <Button
        className={classes.shareButton}
        onClick={() => {
          trackShareQuiz({
            disease: id,
            method: 'line',
            url: shareURL,
            callback: () => {
              window.location.href = `https://line.me/R/msg/text/${encodeURIComponent(shareURL)}`;
            },
          });
        }}
      >
        <img src={LineIcon} alt="share to LINE" style={{ width: 32, height: 32 }} />
      </Button>
      <Button
        className={classes.shareButton}
        onClick={() => {
          trackShareQuiz({
            disease: id,
            method: 'facebook',
            url: shareURL,
            callback: () => {
              window.location.href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareURL)}`;
            },
          });
        }}
      >
        <img src={FacebookIcon} alt="share to Facebook" style={{ width: 32, height: 32 }} />
      </Button>
      {navigator.share && navigator.canShare && navigator.canShare(shareData) && (
        <Button
          className={classes.shareButton}
          onClick={() => {
            trackShareQuiz({
              disease: id,
              method: 'instagram',
              url: shareURL,
              callback: () => {
                navigator.share(shareData);
              },
            });
          }}
        >
          <img src={InstagramIcon} alt="share to Instagram" style={{ width: 32, height: 32 }} />
        </Button>
      )}
      <Button
        className={classes.shareButton}
        onClick={() => {
          trackShareQuiz({
            disease: id,
            method: 'link',
            url: shareURL,
            callback: async () => {
              await copyToClipboard(shareURL);
              setOpen(true);
            },
          });
        }}
      >
        <img src={LinkIcon} alt="copy link" style={{ width: 32, height: 32 }} />
      </Button>
    </Box>
  );
};

export const KnowledgeLevelBadge = ({ prizeStatus, correctNum, preload, config }) => {
  const { id, totalQuestion } = config;
  const allCorrect = correctNum === totalQuestion;
  const canDrawPrize = allCorrect && prizeStatus === PRIZE_STATUS.PRIZE_INIT;
  const headerImage = allCorrect ? prizeBadges[prizeStatus] : testAgainBadges[correctNum];

  const history = useHistory();

  const handleClick = () => {
    if (canDrawPrize) {
      history.push(`/disease-quiz-${id}/prize?liffId=${Liff.id}`);
      return;
    }
    if (!allCorrect) {
      history.goBack();
    }
  };

  if (preload) {
    preloadImage([headerImage]);
    return <Fragment />;
  }

  return (
    <Button fullWidth onClick={handleClick} style={{ padding: 0 }}>
      <img src={headerImage} alt="knowledge level badge" style={{ width: '100%' }} />
    </Button>
  );
};

export default Result;
